import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ENTITIES, TRUSTS } from "@/lib/entities";
import { Loader2, Home, MapPin, CheckCircle2, Bookmark, Compass, Scale, ArrowRight } from "lucide-react";

export default function MyPlan() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => { if (!loading && !user) navigate("/"); }, [loading, user, navigate]);
    useEffect(() => {
        if (user) api.get("/me/plan").then((r) => setData(r.data)).catch(() => setData({ roadmap: null, pins: [] })).finally(() => setBusy(false));
    }, [user]);

    if (loading || busy) return <div className="flex min-h-screen items-center justify-center bg-pale"><Loader2 className="h-6 w-6 animate-spin text-ocean" /></div>;

    const roadmap = data?.roadmap;
    const pins = data?.pins || [];
    const byId = (id) => ENTITIES.find((e) => e.id === id) || TRUSTS.find((t) => t.id === id);

    return (
        <div className="min-h-screen bg-pale" data-testid="myplan-page">
            <div className="border-b border-slate-200 bg-navy px-4 py-4 text-white lg:px-8">
                <div className="mx-auto flex max-w-4xl items-center justify-between">
                    <span className="font-heading text-sm font-bold">Smarter Choice · My Plan</span>
                    <Button size="sm" variant="ghost" onClick={() => navigate("/")} className="text-white hover:bg-white/10"><Home className="mr-1.5 h-4 w-4" /> Home</Button>
                </div>
            </div>

            <div className="mx-auto max-w-4xl px-4 py-10 lg:px-8">
                <h1 className="font-heading text-3xl font-bold text-navy sm:text-4xl">My Launch Plan</h1>
                <p className="mt-3 text-slate-600">Your saved roadmap and the structures you've pinned to discuss.</p>

                {/* Pinned structures */}
                <div className="mt-8">
                    <div className="mb-3 flex items-center gap-2 text-navy"><Bookmark className="h-5 w-5 text-ocean" /><h2 className="font-heading text-lg font-bold">Pinned structures</h2></div>
                    {pins.length === 0 ? (
                        <div className="si-card p-6 text-sm text-slate-600" data-testid="myplan-no-pins">
                            You haven't pinned any entities or trusts yet. <button onClick={() => navigate("/compare")} className="font-semibold text-ocean hover:underline">Compare entities & trusts</button> and tap the bookmark to save your top choices.
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2" data-testid="myplan-pins">
                            {pins.map((p) => {
                                const it = byId(p.id);
                                return (
                                    <div key={p.id} className="si-card p-5">
                                        <div className="flex items-center gap-2">
                                            {p.kind === "trust" ? <Scale className="h-4 w-4 text-teal" /> : <CheckCircle2 className="h-4 w-4 text-teal" />}
                                            <h3 className="font-heading text-sm font-bold text-navy">{p.name}</h3>
                                        </div>
                                        {it && <p className="mt-1.5 text-sm text-slate-600">{it.tagline}</p>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Saved roadmap */}
                <div className="mt-10">
                    <div className="mb-3 flex items-center gap-2 text-navy"><MapPin className="h-5 w-5 text-ocean" /><h2 className="font-heading text-lg font-bold">Saved roadmap</h2></div>
                    {!roadmap ? (
                        <div className="si-card p-6 text-sm text-slate-600" data-testid="myplan-no-roadmap">
                            No saved roadmap yet. <button onClick={() => navigate("/strategizer")} className="font-semibold text-ocean hover:underline">Build your free roadmap</button> and save it here.
                        </div>
                    ) : (
                        <div data-testid="myplan-roadmap">
                            {roadmap.vision_summary && <p className="si-card p-5 text-sm leading-relaxed text-slate-700">{roadmap.vision_summary}</p>}
                            <div className="mt-4 space-y-3">
                                {(roadmap.steps || []).map((s, i) => (
                                    <div key={i} className="si-card p-5">
                                        <div className="flex items-start gap-3">
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-bold text-white">{i + 1}</div>
                                            <div>
                                                <h3 className="font-heading text-base font-bold text-navy">{s.title}</h3>
                                                <ul className="mt-2 space-y-1.5">{(s.actions || []).map((a, j) => <li key={j} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal" /> {a}</li>)}</ul>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                    <Button onClick={() => navigate("/strategizer")} data-testid="myplan-strategizer" className="bg-navy font-semibold text-white hover:bg-navy-light"><Compass className="mr-2 h-4 w-4" /> Update my roadmap</Button>
                    <Button onClick={() => navigate("/services/business-launch")} variant="outline" data-testid="myplan-service" className="border-slate-300 font-semibold text-navy hover:bg-slate-100">See the Done-With-You Launch <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </div>
            </div>
        </div>
    );
}
